import React from "react";
import { Separator } from "../../ui/separator";

/**
 * RecorderCostSummary — the per-publish cost breakdown shown at the
 * bottom of the publish form. Two flavours:
 *   - commission mode → "Publishing cost covered by EP, you net +bounty"
 *   - standard mode   → base + duration + storage + (featured?) totals
 *
 * Props:
 *   commission        — active commission doc or null
 *   totalCost         — pre-computed total (parent owns the math)
 *   videoDuration     — int seconds, drives the "Duration ({n}s)" line
 *   videoStorage      — int days, drives the "Storage ({n} days)" line
 *   videoFeatured     — bool, drives the featured-promotion line
 *   durationCosts     — { 8:0, 16:1, … } cost map
 *   storageCosts      — { 10:0, 20:1, … } cost map
 */
export const RecorderCostSummary = ({
  commission, totalCost, videoDuration, videoStorage, videoFeatured, durationCosts, storageCosts,
}) => {
  if (commission) {
    return (
      <div className="bg-gray-800 rounded-lg p-3" data-testid="commission-cost-summary">
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-400">Publishing cost (paid by EP):</span>
          <span className="text-emerald-400">covered ✓</span>
        </div>
        <div className="flex items-center justify-between text-sm mt-1">
          <span className="text-gray-400">Bounty (paid to you):</span>
          <span className="text-amber-400">+{(commission.bounty_credits || 0).toFixed(2)}</span>
        </div>
        <Separator className="my-2 bg-gray-700" />
        <div className="flex items-center justify-between font-semibold">
          <span className="text-white">Net to you:</span>
          <span className="text-amber-400">+{(commission.bounty_credits || 0).toFixed(2)} credits</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-800 rounded-lg p-3">
      <div className="flex items-center justify-between">
        <span className="text-gray-300">Base cost:</span>
        <span className="text-white">0.25</span>
      </div>
      {durationCosts[videoDuration] > 0 && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-400">Duration ({videoDuration}s):</span>
          <span className="text-emerald-400">+{durationCosts[videoDuration].toFixed(2)}</span>
        </div>
      )}
      {storageCosts[videoStorage] > 0 && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-400">Storage ({videoStorage} days):</span>
          <span className="text-cyan-400">+{storageCosts[videoStorage].toFixed(2)}</span>
        </div>
      )}
      {videoFeatured && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-400">Featured promotion:</span>
          <span className="text-amber-400">+3.00</span>
        </div>
      )}
      <Separator className="my-2 bg-gray-700" />
      <div className="flex items-center justify-between font-semibold">
        <span className="text-white">Total:</span>
        <span className="text-emerald-400">{totalCost.toFixed(2)} credits</span>
      </div>
    </div>
  );
};
